import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { useState, useEffect, lazy, Suspense } from "react";
import { Loader2 } from "lucide-react";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { ThemeProvider } from "@/context/ThemeContext";
import { LanguageProvider } from "@/i18n";
import AuthPage from "@/components/AuthPage";
import Home from "@/components/Home";
import Onboarding from "@/components/Onboarding";
import ResetPassword from "@/components/ResetPassword";
import { SeoManager } from "@/components/SeoManager";
import { GoogleAuthCallback } from "@/components/GoogleAuthCallback";

const Layout = lazy(() => import("@/components/Layout"));
const MapView = lazy(() => import("@/components/MapView"));
const Marketplace = lazy(() => import("@/components/Marketplace"));
const ListingDetail = lazy(() => import("@/components/ListingDetail"));
const Wallet = lazy(() => import("@/components/Wallet"));
const Trades = lazy(() => import("@/components/Trades"));
const Profile = lazy(() => import("@/components/Profile"));
const Admin = lazy(() => import("@/components/Admin"));
const SearchPage = lazy(() => import("@/components/SearchPage"));
const Messages = lazy(() => import("@/components/Messages"));
const PaymentSuccess = lazy(() => import("@/components/PaymentResult").then((module) => ({ default: module.PaymentSuccess })));
const PaymentCancel = lazy(() => import("@/components/PaymentResult").then((module) => ({ default: module.PaymentCancel })));

const PageLoader = () => (
  <div className="min-h-screen flex items-center justify-center bg-[#fdfbf7]" data-testid="page-loader">
    <Loader2 className="w-8 h-8 animate-spin text-[#d97706]" />
  </div>
);

function Shell() {
  const { user } = useAuth();
  const [showOnboarding, setShowOnboarding] = useState(false);

  useEffect(() => {
    if (user && !localStorage.getItem("deuro_onboarded")) {
      setShowOnboarding(true);
    }
  }, [user]);

  const closeOnboarding = () => {
    localStorage.setItem("deuro_onboarded", "1");
    setShowOnboarding(false);
  };

  if (user === undefined) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#fdfbf7]">
        <Loader2 className="w-8 h-8 animate-spin text-[#d97706]" />
      </div>
    );
  }

  if (!user) {
    return (
      <Routes>
        <Route path="/" element={<div className="min-h-screen bg-[#fdfbf7]"><Home /></div>} />
        <Route path="/accedi" element={<AuthPage />} />
        <Route path="/registrati" element={<AuthPage />} />
        <Route path="/reset-password" element={<ResetPassword />} />
        <Route path="*" element={<Navigate to="/accedi" replace />} />
      </Routes>
    );
  }

  return (
    <>
      {showOnboarding && <Onboarding onClose={closeOnboarding} />}
      <Routes>
        <Route path="/payment/success" element={<PaymentSuccess />} />
        <Route path="/payment/cancel" element={<PaymentCancel />} />
        <Route path="/reset-password" element={<ResetPassword />} />
        <Route path="/*" element={
          <Layout onShowOnboarding={() => setShowOnboarding(true)}>
            <Routes>
              <Route path="/" element={<Home />} />
              <Route path="/mappa" element={<MapView />} />
              <Route path="/mercato" element={<Marketplace />} />
              <Route path="/mercato/:id" element={<ListingDetail />} />
              <Route path="/scambi" element={<Trades />} />
              <Route path="/wallet" element={<Wallet />} />
              <Route path="/profilo" element={<Profile />} />
              <Route path="/utente/:id" element={<Profile />} />
              <Route path="/cerca" element={<SearchPage />} />
              <Route path="/messaggi" element={<Messages />} />
              <Route path="/messaggi/:id" element={<Messages />} />
              <Route path="/admin" element={<Admin />} />
              <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
          </Layout>
        } />
      </Routes>
    </>
  );
}

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <SeoManager />
        <LanguageProvider>
          <AuthProvider>
            <ThemeProvider>
              <Suspense fallback={<PageLoader />}>
                <AppRouter />
              </Suspense>
            </ThemeProvider>
          </AuthProvider>
        </LanguageProvider>
      </BrowserRouter>
    </div>
  );
}

function AppRouter() {
  const location = useLocation();
  if (location.hash?.includes("session_id=")) return <GoogleAuthCallback />;
  return <Shell />;
}

export default App;
